import type { WaitingDelivery } from '@/lib/waiting-state'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { WaitingIndicator } from './WaitingIndicator'
import { isNumericOption } from '@/lib/play.ts'
import type { GameStatusData, NextInput, SubmitActionInput } from '@/lib/types'
import {
  constrainMagicianResponseOption,
  getMagicianActionLabelKey,
  hasInvalidMagicianPairSelection,
} from '@/lib/magician-action-input'
import {
  applyWitchPotionSelection,
  hasConflictingWitchPotionSelection,
} from '@/lib/witch-action-input'
import type { RoundSubPhaseType } from '@repo/db'
import { SUBPHASE_EMOJIS } from '@repo/sdk'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ResponseInput } from './ResponseInput'

interface ActionFormProps {
  nextInput: NextInput
  inputs: Record<string, string | number>
  isSubmitting: boolean
  canAct: boolean
  canSubmit: boolean
  onInputChange: (tag: string, value: string | number) => void
  onSubmit: () => void
}

function ActionForm({
  nextInput,
  inputs,
  isSubmitting,
  canAct,
  canSubmit,
  onInputChange,
  onSubmit,
}: ActionFormProps) {
  const { t } = useI18n()

  const visibleOptions = nextInput?.options.filter((option) => {
    if (option.tag === 'x-response-lucky-skill') {
      const dealerTarget = inputs['x-response-number']
      if (dealerTarget === undefined || dealerTarget === '' || Number(dealerTarget) === -1) {
        return false
      }
    }
    return true
  }) ?? []

  return (
    <CardContent
      className="min-h-0 flex-1 space-y-4 pb-4 pr-4"
      data-testid="action-form"
    >
      {visibleOptions.map((option) => {
        const displayedOption = constrainMagicianResponseOption(option, inputs)
        const actionKey
          = getMagicianActionLabelKey(option.tag) ?? `actions.${option.dbAction}`
        const prompt
          = nextInput?.contextType === 'context_mechanical_wolf_chase'
            && option.tag === 'x-response-number'
            ? nextInput?.prompt
            : t(actionKey)

        return (
          <div
            key={option.tag}
            className="space-y-2"
            data-testid="response-option"
            data-response-tag={option.tag}
          >
            <Label>{prompt}</Label>
            <ResponseInput
              option={displayedOption}
              value={inputs[option.tag]}
              onChange={v => onInputChange(option.tag, v)}
            />
          </div>
        )
      })}

      <Button
        className="w-full"
        disabled={isSubmitting || !canAct || !canSubmit}
        onClick={onSubmit}
      >
        {isSubmitting ? t('play.submitting') : t('play.confirmAction')}
      </Button>
    </CardContent>
  )
}

interface ActionPanelProps {
  className?: string
  seatControl?: ReactNode
  takeoverCountdown?: ReactNode
  nextInput: NextInput
  isSubmitting: boolean
  canAct: boolean
  roundName: string
  gameData?: GameStatusData
  waitingOn?: GameStatusData['waitingOn']
  connection?: {
    delivery?: WaitingDelivery
    isFetchFailing?: boolean
    lastSuccessAt?: number | null
  }
  onSubmit: (responses: SubmitActionInput['responses']) => void
}

export function ActionPanel({
  className,
  seatControl,
  takeoverCountdown,
  nextInput,
  isSubmitting,
  canAct,
  roundName,
  gameData,
  waitingOn,
  connection,
  onSubmit,
}: ActionPanelProps) {
  const [inputs, setInputs] = useState<Record<string, string | number>>({})
  const { t } = useI18n()

  const inputString = useMemo(() => JSON.stringify(nextInput), [nextInput])
  useEffect(() => setInputs({}), [inputString])

  const canSubmit = useMemo(() => {
    if (!nextInput) {
      return false
    }
    if (hasConflictingWitchPotionSelection(inputs)) {
      return false
    }
    if (hasInvalidMagicianPairSelection(inputs)) {
      return false
    }
    return nextInput.options.every((opt) => {
      if (opt.optional === true) {
        return true
      }
      if (opt.tag === 'x-response-lucky-skill') {
        const dealerTarget = inputs['x-response-number']
        if (dealerTarget === undefined || dealerTarget === '' || Number(dealerTarget) === -1) {
          return true
        }
      }
      const val = inputs[opt.tag]
      return val !== undefined && val !== '' && val !== null
    })
  }, [nextInput, inputs])

  const availableResponseTags = useMemo(
    () => new Set(nextInput?.options.map(option => option.tag) ?? []),
    [nextInput],
  )

  const handleInputChange = (tag: string, value: string | number) => {
    setInputs(prev => applyWitchPotionSelection(
      prev,
      tag,
      value,
      availableResponseTags,
    ))
  }

  const handleSubmit = () => {
    if (!nextInput) {
      return
    }
    const responses: Array<{ tag: string, value: string | number }> = []

    const dealerTarget = inputs['x-response-number']
    const isDealerNoTrade = dealerTarget !== undefined && Number(dealerTarget) === -1

    for (const opt of nextInput.options) {
      if (opt.tag === 'x-response-lucky-skill' && isDealerNoTrade) {
        continue
      }

      const val = inputs[opt.tag]
      if (!opt.optional && !val) {
        toast(`Please provide a value for ${opt.tag}`)
        return
      }

      if (val !== undefined && val !== '') {
        responses.push({ tag: opt.tag, value: isNumericOption(opt) ? Number(val) : String(val) })
      }
    }

    onSubmit(responses)
  }

  const subPhase = gameData?.game?.subPhase as RoundSubPhaseType | undefined
  const emoji = subPhase ? SUBPHASE_EMOJIS[subPhase] : '🎮'
  const shouldHideSubphaseDetail = !!gameData?.game?.shouldHideSubphaseDetail

  const currentPlayer = gameData?.humanPlayer?.id
    ? gameData.players.find(p => p.id === gameData.humanPlayer!.id)
    : null

  const gameStatus = gameData?.game?.status
  const showActionForm = !!nextInput
  const showWaitingForOthers = !nextInput
    && gameStatus === 'paused'
    && (waitingOn?.length ?? 0) > 0
    && !shouldHideSubphaseDetail

  return (
    <Card
      data-testid="action-panel"
      className={cn(
        'max-h-full min-h-0 overflow-y-auto overscroll-contain border-primary/20 bg-primary/5 shadow-lg shadow-primary/5 backdrop-blur-sm [scrollbar-gutter:stable]',
        className,
      )}
    >
      <CardHeader className="shrink-0 gap-x-2" data-testid="action-panel-header">
        <CardTitle className="flex min-w-0 flex-wrap items-center gap-2 text-lg font-medium text-primary">
          {
            shouldHideSubphaseDetail ? (
              <>
                <span className="animate-spin text-xl">⏳</span>
                {t('play.waiting.default')}
              </>
            ) : (
              <>
                <span className="text-xl">{emoji}</span>
                {roundName}
                {showActionForm && (
                  <span
                    data-testid="your-turn"
                    className="rounded-full bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground"
                  >
                    {t('play.yourTurn')}
                  </span>
                )}
                {currentPlayer && (
                  <span className="w-full truncate text-sm font-normal sm:ml-auto sm:w-auto">
                    {t('play.playerLabel')} {currentPlayer.position}: {currentPlayer.name} ({t(`game.${currentPlayer.role}`) || currentPlayer.role})
                  </span>
                )}
              </>
            )
          }
        </CardTitle>
        {seatControl && (
          <CardAction
            className="row-span-1"
            data-testid="action-panel-seat-control"
          >
            {seatControl}
          </CardAction>
        )}
        {takeoverCountdown && (
          <div
            className={cn(
              'row-start-2 min-w-0 max-w-full justify-self-end pt-1',
              seatControl ? 'col-span-2' : 'col-span-1',
            )}
            data-testid="action-panel-takeover-countdown"
          >
            {takeoverCountdown}
          </div>
        )}
      </CardHeader>
      {showActionForm
        ? (
            <ActionForm
              nextInput={nextInput}
              inputs={inputs}
              isSubmitting={isSubmitting}
              canAct={canAct}
              canSubmit={canSubmit}
              onInputChange={handleInputChange}
              onSubmit={handleSubmit}
            />
          )
        : showWaitingForOthers
          ? (
              <CardContent className="min-h-0 flex-1 overflow-y-auto">
                <WaitingIndicator
                  gameStatus={gameData?.game?.status}
                  delivery={connection?.delivery}
                  isFetchFailing={connection?.isFetchFailing}
                  lastSuccessAt={connection?.lastSuccessAt}
                  shouldHideSubphaseDetail={shouldHideSubphaseDetail}
                  waitingOnNames={waitingOn?.map(w => w.name)}
                />
              </CardContent>
            )
          : (
              <CardContent className="min-h-0 flex-1 overflow-y-auto">
                <WaitingIndicator
                  gameStatus={gameData?.game?.status}
                  delivery={connection?.delivery}
                  isFetchFailing={connection?.isFetchFailing}
                  lastSuccessAt={connection?.lastSuccessAt}
                  shouldHideSubphaseDetail={shouldHideSubphaseDetail}
                  testId="waiting-state"
                  thinkingSeatNames={
                    shouldHideSubphaseDetail
                      ? []
                      : gameData?.players
                          ?.filter(p => p.isThinking)
                          .map(p => p.name)
                  }
                />
              </CardContent>
            )}
    </Card>
  )
}
